import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { TreeView, TreeItem } from '@mui/x-tree-view';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import { Category } from '../../types/Category';
import { Document } from '../../types/Document';
import {
  categoryPickerNodeExpandable,
  contentCategoryMayHaveRows,
  contentCountForPickerDisplay,
  findCategoryInTree,
} from '../../utils/CategoryUtils';
import {
  branchChildrenAreLoaded,
  categoryContentsAreLoaded,
} from '../../utils/categoryLoadState';
import {
  fetchCategoryForPane,
  fetchCategoryWithChildren,
  mergeBranchFetchIntoTree,
  mergeContentsFetchIntoTree,
} from '../../services/CategoryService';
import api from '../../services/api';

export interface ImportSubtreeFromDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  documents: Document[];
  /** Omit from source list (usually the active document). */
  excludeDocumentId?: string;
  /** Current tab name shown for context */
  targetDocumentLabel?: string;
  /** Category that will receive the cloned subtree */
  targetCategoryName: string;
  onRefreshDocuments?: () => void | Promise<void>;
  onConfirm: (sourceSubtreeRoot: Category) => void | Promise<void>;
}

const ImportSubtreeFromDocumentDialog: React.FC<ImportSubtreeFromDocumentDialogProps> = ({
  open,
  onClose,
  documents,
  excludeDocumentId,
  targetDocumentLabel,
  targetCategoryName,
  onRefreshDocuments,
  onConfirm,
}) => {
  const [selectedDocId, setSelectedDocId] = useState<string>('');
  /** Stable fetch key; avoids re-fetch loops when `documents` churns (e.g. subcase refreshes). */
  const [sourceRootCategoryId, setSourceRootCategoryId] = useState<string | null>(null);
  const [sourceDocumentName, setSourceDocumentName] = useState<string | null>(null);
  const [pickerTree, setPickerTree] = useState<Category[]>([]);
  const [expandedNodeIds, setExpandedNodeIds] = useState<string[]>([]);
  const [loadingTree, setLoadingTree] = useState(false);
  const [treeError, setTreeError] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [treeLoadingIds, setTreeLoadingIds] = useState<Set<string>>(new Set());
  const pickerTreeRef = useRef<Category[]>([]);
  pickerTreeRef.current = pickerTree;
  const loadGenRef = useRef(0);

  const docsWithCategory = useMemo(
    () =>
      [...documents]
        .filter(
          (d) =>
            d.categoryId &&
            (!excludeDocumentId || d.id !== excludeDocumentId),
        )
        .sort((a, b) => (a.label || a.name).localeCompare(b.label || b.name)),
    [documents, excludeDocumentId],
  );

  const sourceRoot = pickerTree[0] ?? null;

  useEffect(() => {
    if (!open) return;
    void onRefreshDocuments?.();
    // Intentionally omit onRefreshDocuments from deps — identity changes would re-fetch the doc list endlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSelectedDocId('');
      setSourceRootCategoryId(null);
      setSourceDocumentName(null);
      setPickerTree([]);
      pickerTreeRef.current = [];
      setExpandedNodeIds([]);
      setLoadingTree(false);
      setTreeError(null);
      setSelectedNodeId(null);
      setSubmitting(false);
      setTreeLoadingIds(new Set());
      return;
    }
    setSelectedDocId('');
    setSourceRootCategoryId(null);
    setSourceDocumentName(null);
    setPickerTree([]);
    pickerTreeRef.current = [];
    setExpandedNodeIds([]);
    setTreeError(null);
    setSelectedNodeId(null);
  }, [open]);

  const hydrateContentCategoryCounts = useCallback(async (categoryIds: string[]) => {
    const toLoad = categoryIds.filter((id) => {
      const live = findCategoryInTree(pickerTreeRef.current, id);
      return (
        !!live?.contentCategory &&
        !categoryContentsAreLoaded(live) &&
        contentCategoryMayHaveRows(live)
      );
    });
    if (toLoad.length === 0) return;

    setTreeLoadingIds((prev) => {
      const next = new Set(prev);
      toLoad.forEach((id) => next.add(id));
      return next;
    });
    try {
      const results = await Promise.all(
        toLoad.map(async (id) => {
          try {
            return { id, loaded: await fetchCategoryForPane(id) };
          } catch {
            return null;
          }
        }),
      );
      setPickerTree((prev) => {
        let next = prev;
        for (const result of results) {
          if (!result) continue;
          next = mergeContentsFetchIntoTree(next, result.id, result.loaded);
        }
        pickerTreeRef.current = next;
        return next;
      });
    } finally {
      setTreeLoadingIds((prev) => {
        const next = new Set(prev);
        toLoad.forEach((id) => next.delete(id));
        return next;
      });
    }
  }, []);

  const ensurePickerNodeLoaded = useCallback(
    async (categoryId: string) => {
      const live = findCategoryInTree(pickerTreeRef.current, categoryId);
      if (!live) return null;
      if (live.contentCategory) {
        if (categoryContentsAreLoaded(live)) return live;
        setTreeLoadingIds((prev) => new Set(prev).add(categoryId));
        try {
          const loaded = await fetchCategoryForPane(categoryId);
          setPickerTree((prev) => {
            const next = mergeContentsFetchIntoTree(prev, categoryId, loaded);
            pickerTreeRef.current = next;
            return next;
          });
          return loaded;
        } finally {
          setTreeLoadingIds((prev) => {
            const next = new Set(prev);
            next.delete(categoryId);
            return next;
          });
        }
      }
      if (branchChildrenAreLoaded(live)) return live;
      setTreeLoadingIds((prev) => new Set(prev).add(categoryId));
      try {
        const loaded = await fetchCategoryForPane(categoryId);
        setPickerTree((prev) => {
          const next = mergeBranchFetchIntoTree(prev, categoryId, loaded);
          pickerTreeRef.current = next;
          return next;
        });
        const contentIds = (loaded.children ?? [])
          .filter((c) => c.contentCategory && contentCategoryMayHaveRows(c))
          .map((c) => c.id);
        if (contentIds.length > 0) {
          void hydrateContentCategoryCounts(contentIds);
        }
        return loaded;
      } finally {
        setTreeLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(categoryId);
          return next;
        });
      }
    },
    [hydrateContentCategoryCounts],
  );

  // Load only the document root level when a source document is chosen.
  useEffect(() => {
    if (!open || !sourceRootCategoryId || !sourceDocumentName) {
      setPickerTree([]);
      pickerTreeRef.current = [];
      setTreeError(null);
      setSelectedNodeId(null);
      setExpandedNodeIds([]);
      setLoadingTree(false);
      return;
    }

    const gen = ++loadGenRef.current;
    setLoadingTree(true);
    setTreeError(null);
    setPickerTree([]);
    pickerTreeRef.current = [];
    setSelectedNodeId(null);
    setExpandedNodeIds([]);

    void (async () => {
      try {
        await api.post('/documents/open', null, {
          params: { documentId: sourceDocumentName },
        });
        const root = await fetchCategoryForPane(sourceRootCategoryId);
        if (gen !== loadGenRef.current) return;
        setPickerTree([root]);
        pickerTreeRef.current = [root];
        setExpandedNodeIds([root.id]);
        setSelectedNodeId(root.id);
        // Load counts for content categories visible under the root.
        if (!root.contentCategory && branchChildrenAreLoaded(root)) {
          const contentIds = (root.children ?? [])
            .filter((c) => c.contentCategory && contentCategoryMayHaveRows(c))
            .map((c) => c.id);
          if (contentIds.length > 0) {
            void hydrateContentCategoryCounts(contentIds);
          }
        } else if (root.contentCategory && contentCategoryMayHaveRows(root)) {
          void hydrateContentCategoryCounts([root.id]);
        }
      } catch (e) {
        if (gen !== loadGenRef.current) return;
        console.error(e);
        setTreeError(
          e instanceof Error ? e.message : 'Failed to load category structure',
        );
      } finally {
        if (gen === loadGenRef.current) setLoadingTree(false);
      }
    })();
  }, [open, sourceRootCategoryId, sourceDocumentName, hydrateContentCategoryCounts]);

  // Hydrate counts for content categories under expanded nodes.
  useEffect(() => {
    if (!open || pickerTree.length === 0) return;
    const expanded = new Set(expandedNodeIds);
    const ids: string[] = [];
    const walk = (cat: Category, parentExpanded: boolean) => {
      if (cat.contentCategory) {
        if (
          parentExpanded &&
          contentCategoryMayHaveRows(cat) &&
          !categoryContentsAreLoaded(cat)
        ) {
          ids.push(cat.id);
        }
        return;
      }
      if (!expanded.has(cat.id)) return;
      for (const child of cat.children ?? []) {
        walk(child, true);
      }
    };
    for (const root of pickerTree) {
      if (root.contentCategory) walk(root, true);
      else if (expanded.has(root.id)) {
        for (const child of root.children ?? []) walk(child, true);
      }
    }
    if (ids.length > 0) {
      void hydrateContentCategoryCounts(ids);
    }
  }, [open, pickerTree, expandedNodeIds, hydrateContentCategoryCounts]);

  const handleNodeToggle = (_event: React.SyntheticEvent, nodeIds: string[]) => {
    setExpandedNodeIds(nodeIds);
    const newlyExpanded = nodeIds.filter((id) => !expandedNodeIds.includes(id));
    for (const id of newlyExpanded) {
      const cat = findCategoryInTree(pickerTreeRef.current, id);
      if (cat && !cat.contentCategory && !branchChildrenAreLoaded(cat)) {
        void ensurePickerNodeLoaded(id);
      } else if (cat && !cat.contentCategory && branchChildrenAreLoaded(cat)) {
        const contentIds = (cat.children ?? [])
          .filter((c) => c.contentCategory && contentCategoryMayHaveRows(c))
          .map((c) => c.id);
        if (contentIds.length > 0) {
          void hydrateContentCategoryCounts(contentIds);
        }
      }
    }
  };

  const handleNodeSelect = (_event: React.SyntheticEvent, nodeIdInput: string | string[]) => {
    const nodeId = Array.isArray(nodeIdInput) ? nodeIdInput[0] : nodeIdInput;
    if (!nodeId || nodeId.endsWith('__loading')) return;
    setSelectedNodeId(nodeId);
  };

  const renderTreeItems = (category: Category): React.ReactNode => {
    const expandable = categoryPickerNodeExpandable(category);
    const childrenLoaded = branchChildrenAreLoaded(category);
    const hasLoadedChildren = (category.children?.length ?? 0) > 0;
    const loading = treeLoadingIds.has(category.id);
    const rowCount = contentCountForPickerDisplay(category);

    const label = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}>
        {category.contentCategory ? (
          <DescriptionIcon fontSize="small" color="action" />
        ) : (
          <FolderIcon fontSize="small" color="action" />
        )}
        <Typography variant="body2">
          {category.name}
          {loading && (
            <Typography component="span" variant="caption" color="text.secondary">
              {' '}
              (loading…)
            </Typography>
          )}
          {category.contentCategory && (
            <Typography component="span" variant="caption" color="text.secondary">
              {` (${rowCount === null ? '…' : rowCount})`}
            </Typography>
          )}
        </Typography>
      </Box>
    );

    // Content categories must not pass children — MUI shows a chevron otherwise.
    if (category.contentCategory) {
      return (
        <TreeItem key={category.id} nodeId={category.id} label={label} />
      );
    }

    return (
      <TreeItem key={category.id} nodeId={category.id} label={label}>
        {hasLoadedChildren
          ? category.children!.map((child) => renderTreeItems(child))
          : null}
        {expandable && !childrenLoaded && !hasLoadedChildren ? (
          <TreeItem
            nodeId={`${category.id}__loading`}
            label={
              <Typography variant="body2" color="text.secondary">
                Loading…
              </Typography>
            }
            disabled
          />
        ) : null}
      </TreeItem>
    );
  };

  const handleConfirm = async () => {
    if (!selectedNodeId) return;
    setSubmitting(true);
    setTreeError(null);
    try {
      // Full subtree of the selection only — needed for clone; display stays lazy.
      const fullSubtree = await fetchCategoryWithChildren(selectedNodeId);
      await onConfirm(fullSubtree);
      onClose();
    } catch (e) {
      console.error(e);
      setTreeError(
        `Import failed: ${e instanceof Error ? e.message : 'Unknown error'}`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Import branch from document</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Copy an entire branch from another document — the category you pick below, plus all
          categories and content beneath it — into{' '}
          <strong>{targetCategoryName || '(no category selected)'}</strong>
          {targetDocumentLabel ? (
            <>
              {' '}
              in <strong>{targetDocumentLabel}</strong>
            </>
          ) : null}
          .
        </Typography>

        {docsWithCategory.length === 0 ? (
          <Alert severity="info">No documents available.</Alert>
        ) : (
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel id="import-subtree-source-doc">Source document</InputLabel>
            <Select
              labelId="import-subtree-source-doc"
              label="Source document"
              value={selectedDocId}
              onChange={(e) => {
                const id = e.target.value as string;
                setSelectedDocId(id);
                const doc = docsWithCategory.find((d) => d.id === id);
                setSourceRootCategoryId(doc?.categoryId ?? null);
                setSourceDocumentName(doc?.name ?? null);
              }}
            >
              {docsWithCategory.map((doc) => (
                <MenuItem key={doc.id} value={doc.id}>
                  {doc.label || doc.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {loadingTree && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {treeError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {treeError}
          </Alert>
        )}

        {!loadingTree && sourceRoot && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Category to copy (including descendants)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Expand folders to browse. The full branch is loaded only when you import.
            </Typography>
            <Box
              sx={{
                maxHeight: 360,
                overflow: 'auto',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 1,
              }}
            >
              <TreeView
                key={sourceRoot.id}
                defaultCollapseIcon={<ExpandMoreIcon />}
                defaultExpandIcon={<ChevronRightIcon />}
                expanded={expandedNodeIds}
                onNodeToggle={handleNodeToggle}
                selected={selectedNodeId || ''}
                onNodeSelect={handleNodeSelect}
              >
                {renderTreeItems(sourceRoot)}
              </TreeView>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleConfirm()}
          disabled={
            submitting ||
            !sourceRoot ||
            !selectedNodeId ||
            loadingTree ||
            docsWithCategory.length === 0
          }
        >
          {submitting ? 'Importing…' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportSubtreeFromDocumentDialog;
